/**
 * A changeset in plain words: what kinds of things it changes ("3 deliverables moved", "2 projects' rates
 * updated") and one sentence that says it all ("Create a project, move 3 deliverables and 4 tasks, and
 * update both projects' rates").
 *
 * Built from the recorded writes (`GET /changesets/:id?ops=true`), so it's there the moment the card loads.
 * The backend can return a model-written `recap` too; the card prefers it when it arrives.
 */
import { ARTIFACT_KINDS, type ArtifactType, type ChangesetOp } from './artifacts'

export type ChangeVerb = 'create' | 'move' | 'remove' | 'rates' | 'status' | 'dates' | 'rename' | 'update' | 'send'

export interface ChangeGroup {
    type: string
    verb: ChangeVerb
    count: number
    /** "3 deliverables moved" */
    label: string
    icon: string
}

const ALIASES: Record<string, string> = { email_thread: 'thread', contract: 'agreement', pitch: 'outreach', invoice_line: 'invoice' }
/** Internal records never shown as changes. */
const HIDDEN = new Set(['memory_file', 'knowledge', 'script_comment', 'project_note', 'inbox', 'agreement_issue', 'checkin', 'goal', 'deal'])
const NOUNS: Record<string, [string, string]> = {
    script: ['script', 'scripts'], checkin: ['check-in', 'check-ins'], goal: ['goal', 'goals'],
}
const PAST: Record<ChangeVerb, string> = {
    create: 'created', move: 'moved', remove: 'removed', rates: 'rate updated', status: 'status changed',
    dates: 'dates changed', rename: 'renamed', update: 'updated', send: 'sent',
}
/** Most telling first, so the sentence leads with the big move. */
const VERB_ORDER: ChangeVerb[] = ['create', 'move', 'send', 'rates', 'status', 'dates', 'rename', 'update', 'remove']
const ICONS: Record<ChangeVerb, string> = {
    create: 'fa-plus', move: 'fa-arrow-right-arrow-left', remove: 'fa-trash-can', rates: 'fa-dollar-sign',
    status: 'fa-flag', dates: 'fa-calendar', rename: 'fa-pen', update: 'fa-pen', send: 'fa-paper-plane',
}

function noun(type: string, n: number): string {
    const k = ARTIFACT_KINDS[type as ArtifactType]
    const pair = NOUNS[type] ?? (k ? [k.label.toLowerCase(), k.plural] : [type.replace(/_/g, ' '), `${type.replace(/_/g, ' ')}s`])
    return n === 1 ? pair[0] : pair[1]
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/** What an update did, from the fields it touched. */
function verbForUpdate(before: Record<string, any> | null, after: Record<string, any> | null): ChangeVerb {
    const keys = Object.keys(after ?? {}).filter((k) => !/^(updated_at|last_updated|version|modified_at)$/.test(k) && !same(before?.[k], after?.[k]))
    const has = (re: RegExp) => keys.some((k) => re.test(k))
    if (after?.deleted_at && !before?.deleted_at) return 'remove'
    if (has(/^(project|project_id|deal|deal_id|thread|parent)$/)) return 'move'
    if (has(/rate|price|amount|fee|total|budget/)) return 'rates'
    if (has(/status|stage/)) return 'status'
    if (has(/date|due|deadline|_at$/)) return 'dates'
    if (keys.length && keys.every((k) => /^(title|name|subject)$/.test(k))) return 'rename'
    return 'update'
}

/** The changes, grouped by what kind of record and what happened to it. */
export function changeGroups(ops: ChangesetOp[] | null | undefined): ChangeGroup[] {
    const seen = new Map<string, { type: string; verb: ChangeVerb; ids: Set<string> }>()
    for (const o of ops ?? []) {
        const raw = o.object_type ?? ''
        if (!raw || HIDDEN.has(raw)) continue
        const type = ALIASES[raw] ?? raw
        const verb: ChangeVerb = o.op === 'insert' ? 'create' : o.op === 'delete' ? 'remove' : verbForUpdate(o.before, o.after)
        // An invoice line belongs to its invoice; a line edit is an update of the invoice.
        const id = raw === 'invoice_line' ? String(o.after?.invoice ?? o.before?.invoice ?? o.pk) : String(o.pk)
        const key = `${type}:${raw === 'invoice_line' && verb === 'create' ? 'update' : verb}`
        const g = seen.get(key) ?? { type, verb: raw === 'invoice_line' && verb === 'create' ? 'update' : verb, ids: new Set<string>() }
        g.ids.add(id)
        seen.set(key, g)
    }
    // A record created in this changeset isn't also "updated" or "moved".
    const created = new Set([...seen.values()].filter((g) => g.verb === 'create').flatMap((g) => [...g.ids].map((id) => `${g.type}:${id}`)))
    const out: ChangeGroup[] = []
    for (const g of seen.values()) {
        const ids = g.verb === 'create' ? [...g.ids] : [...g.ids].filter((id) => !created.has(`${g.type}:${id}`))
        if (!ids.length) continue
        const n = ids.length
        const label = g.verb === 'rates' || g.verb === 'status'
            ? `${n} ${noun(g.type, n)} · ${PAST[g.verb]}`
            : `${n} ${noun(g.type, n)} ${PAST[g.verb]}`
        out.push({ type: g.type, verb: g.verb, count: n, label, icon: ICONS[g.verb] })
    }
    return out.sort((a, b) => VERB_ORDER.indexOf(a.verb) - VERB_ORDER.indexOf(b.verb) || b.count - a.count)
}

function count(n: number, type: string): string {
    const words = ['', 'a', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
    if (n === 2 && type) return `both ${noun(type, 2)}`
    return `${n < 10 ? words[n] || n : n} ${noun(type, n)}`
}

function possessive(n: number, type: string): string {
    if (n === 1) return `the ${noun(type, 1)}'s`
    return n === 2 ? `both ${noun(type, 2)}'` : `${n} ${noun(type, n)}'`
}

/** One readable sentence: "Create a project, move 3 deliverables and 4 tasks, and update both projects' rates." */
export function recapSentence(groups: ChangeGroup[]): string {
    if (!groups.length) return ''
    const byVerb = new Map<ChangeVerb, ChangeGroup[]>()
    for (const g of groups) byVerb.set(g.verb, [...(byVerb.get(g.verb) ?? []), g])
    const list = (gs: ChangeGroup[], f: (g: ChangeGroup) => string) => {
        const parts = gs.map(f)
        return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}` : parts[0]
    }
    const clauses: string[] = []
    for (const verb of VERB_ORDER) {
        const gs = byVerb.get(verb)
        if (!gs) continue
        const things = (g: ChangeGroup) => count(g.count, g.type).replace(/^a (?=[aeiou])/, 'an ')
        switch (verb) {
            case 'create': clauses.push(`create ${list(gs, things)}`); break
            case 'move': clauses.push(`move ${list(gs, things)}`); break
            case 'remove': clauses.push(`remove ${list(gs, things)}`); break
            case 'send': clauses.push(`send ${list(gs, things)}`); break
            case 'rates': clauses.push(`update ${list(gs, (g) => `${possessive(g.count, g.type)} rate${g.count === 1 ? '' : 's'}`)}`); break
            case 'status': clauses.push(`change ${list(gs, (g) => `${possessive(g.count, g.type)} status`)}`); break
            case 'dates': clauses.push(`reschedule ${list(gs, things)}`); break
            case 'rename': clauses.push(`rename ${list(gs, things)}`); break
            case 'update': clauses.push(`update ${list(gs, things)}`); break
        }
    }
    const body = clauses.length > 2 ? `${clauses.slice(0, -1).join(', ')}, and ${clauses.at(-1)}`
        : clauses.length === 2 ? `${clauses[0]} and ${clauses[1]}` : clauses[0]
    return `${body[0].toUpperCase()}${body.slice(1)}.`
}

/** "Draft email …" + "Save/Update draft … in the mailbox drafts" describe one thing; show the first only. */
export function visibleSummary(summary: string[]): string[] {
    return summary.filter((l) => !/^(Save|Update) draft .* (to|in) the mailbox drafts$/.test(l))
}

function joinLines(ls: string[]): string {
    if (ls.length < 2) return ls[0] ?? ''
    const [a, b] = ls
    return `${a.replace(/[.\s]+$/, '')} and ${b[0].toLowerCase()}${b.slice(1).replace(/[.\s]+$/, '')}.`
}

/**
 * What a changeset did, in one sentence: the model's recap when there is one; for one or two changes the
 * agent's own words (they carry names and amounts); for more, the grouped sentence.
 */
export function changesRecap(lines: string[], groups: ChangeGroup[], aiRecap?: string | null): string {
    return aiRecap || (lines.length <= 2 ? joinLines(lines) : recapSentence(groups)) || lines[0] || ''
}
